import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, X, Send, Bot, User, Minimize2, Maximize2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [chatId, setChatId] = useState("");
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const loadHistory = useCallback(async (id) => {
    try {
      const response = await axios.get(`${API}/chat/${id}`);
      if (response.data && response.data.length > 0) {
        setMessages(response.data);
      } else {
        setMessages([{
          id: "greeting",
          role: "assistant",
          text: "Hello! I'm the 305 KAOS assistant. How can I help you with App or SI integrations today?"
        }]);
      }
    } catch (e) {
      // Non-critical: failed to load history; user can still start a new chat.
    }
  }, []);

  useEffect(() => {
    let storedId = localStorage.getItem("kaos_chat_id");
    if (!storedId) {
      storedId = "chat_" + Math.random().toString(36).substring(2, 15);
      localStorage.setItem("kaos_chat_id", storedId);
    }
    setChatId(storedId);
    loadHistory(storedId);
  }, [loadHistory]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen, isMinimized, scrollToBottom]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userText = input.trim();
    setInput("");
    
    // Optimistic UI update
    setMessages(prev => [...prev, { id: crypto.randomUUID(), role: "user", text: userText }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        chat_id: chatId,
        message: userText
      });
      setMessages(prev => [...prev, { id: crypto.randomUUID(), role: "assistant", text: response.data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, { id: crypto.randomUUID(), role: "assistant", text: "Connection error. Please try again." }]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <button 
        onClick={() => { setIsOpen(true); setIsMinimized(false); }}
        className="fixed bottom-6 right-6 w-14 h-14 bg-cyan-500 hover:bg-cyan-400 text-black rounded-full shadow-lg shadow-cyan-500/20 flex items-center justify-center transition-transform hover:scale-110 z-[9999]"
      >
        <MessageSquare className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className={`fixed right-6 bottom-6 w-80 sm:w-96 glass-card border border-white/10 shadow-2xl flex flex-col z-[9999] transition-all duration-300 ease-in-out ${isMinimized ? 'h-16 rounded-2xl overflow-hidden' : 'h-[32rem] rounded-2xl'}`}>
      {/* Header */}
      <div className="h-16 px-4 bg-gradient-to-r from-teal-900/50 to-cyan-900/50 border-b border-white/5 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400">
            <Bot className="w-5 h-5" />
          </div>
          <span className="font-bold text-white tracking-wide">KAOS SI</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setIsMinimized(!isMinimized)} className="text-gray-400 hover:text-white transition-colors">
            {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Body */}
      {!isMinimized && (
        <>
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-black/40">
            {messages.map((msg, idx) => (
              <div key={msg.id || idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${msg.role === 'user' ? 'bg-cyan-500 text-black rounded-tr-sm font-medium' : 'bg-white/10 text-gray-200 border border-white/5 rounded-tl-sm'}`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white/5 border border-white/5 rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1 items-center">
                  <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce"></div>
                  <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce delay-75"></div>
                  <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce delay-150"></div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Footer Input */}
          <form onSubmit={sendMessage} className="p-3 border-t border-white/5 bg-black/60">
            <div className="relative flex items-center">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about SI..."
                className="w-full bg-white/5 border border-white/10 rounded-full py-2.5 pl-4 pr-12 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50"
              />
              <button 
                type="submit"
                disabled={isLoading || !input.trim()}
                className="absolute right-2 w-8 h-8 rounded-full bg-cyan-500 text-black flex items-center justify-center hover:bg-cyan-400 disabled:opacity-50 disabled:hover:bg-cyan-500 transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};
